/**
 * PrivateCompanyBanner - Warning banner for private companies
 *
 * Displays when analyzing a private company to indicate limited public data availability.
 */

interface PrivateCompanyBannerProps {
  companyName?: string
  variant?: 'default' | 'compact'
}

export function PrivateCompanyBanner({ companyName, variant = 'default' }: PrivateCompanyBannerProps) {
  if (variant === 'compact') {
    return (
      <div className="bg-term-info/20 border border-term-info rounded-lg p-3">
        <div className="flex items-center gap-2">
          <span className="text-xl">🔒</span>
          <div>
            <span className="text-term-info font-semibold">Private Company</span>
            <span className="text-term-textDim text-sm ml-2">
              Limited public data available
            </span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-term-info/20 border border-term-info rounded-lg p-4">
      <div className="flex items-center gap-3">
        <span className="text-2xl">🔒</span>
        <div>
          <h3 className="text-term-info font-bold text-lg">
            Private Company{companyName ? ` - ${companyName}` : ''}
          </h3>
          <p className="text-term-textDim text-sm">
            Limited public data available. Financial metrics from public sources may be incomplete or unavailable.
          </p>
          <p className="text-term-textDim text-sm mt-1">
            <span className="text-term-info">💡 Tip:</span> Request data room access for comprehensive analysis.
            Use the Data Room tab to track document collection.
          </p>
        </div>
      </div>
    </div>
  )
}

/**
 * PrivateCompanyNoDataMessage - Message shown when data is unavailable for private companies
 */
interface PrivateCompanyNoDataMessageProps {
  dataType: string
  suggestion?: string
}

export function PrivateCompanyNoDataMessage({ dataType, suggestion }: PrivateCompanyNoDataMessageProps) {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center p-8">
      <div className="text-4xl mb-4">🔒</div>
      <h3 className="text-lg font-semibold text-term-text mb-2">
        {dataType} Not Available
      </h3>
      <p className="text-term-textDim mb-4 max-w-md">
        This is a private company. Public {dataType.toLowerCase()} data is not available
        through standard financial APIs.
      </p>
      {suggestion && (
        <p className="text-term-info text-sm">
          <span className="font-medium">Suggestion:</span> {suggestion}
        </p>
      )}
    </div>
  )
}
